import { Button, Center, Spinner, Text } from "@chakra-ui/react";
import { Box, BoxProps } from "@chakra-ui/react";
import { LatLng } from "leaflet";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { MapContainer, Polygon, TileLayer, useMapEvents } from "react-leaflet";
import { useNavigate } from "react-router";

import { ColumnDefinition, RanaModal, RanaTable } from "@nens/ranalib";
import { ThreediModel } from "@nens/threedi/openapi-client";

import ModelsMapSvg from "@/assets/models.svg?url";

import { APP_BASE_HREF } from "../../App";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import ThreediModelLink from "../../components/ThreediModelLink";
import { OrganisationContext } from "../../providers/OrganisationProvider";
import { useAllThreediModelsByOwner } from "../../queries/threedimodels";
import { WORLD_EXTENT, combineExtents, threediModelExtent } from "../../utils/geo";

import { ModelListHelpText } from "./ModelListHelpText";

const mapBoxAccessToken =
  "pk.eyJ1IjoibmVsZW5zY2h1dXJtYW5zIiwiYSI6ImNrZWlnbHdycjFqNHMyem95cWFqNzhkc3IifQ.ymzd92iqviR5RZ-dd-xRIg";

interface MapWithModelsProps extends BoxProps {
  threediModels: ThreediModel[];
}

const ClickThreediModelsMap: FC<MapWithModelsProps> = ({ threediModels }) => {
  const [mapClick, setMapClick] = useState<LatLng | null>(null);

  useMapEvents({
    click: (event) => {
      setMapClick(event.latlng);
    },
  });

  const modelsAtClick: (ThreediModel & { uuid: string })[] = useMemo(
    () =>
      threediModels
        .filter((threediModel) => {
          if (mapClick === null) return false;
          const modelExtent = threediModelExtent(threediModel);
          return modelExtent && modelExtent.contains(mapClick);
        })
        .map((threediModel) => {
          return {
            ...threediModel,
            uuid: threediModel.id + "",
          };
        }),
    [mapClick, threediModels]
  );

  const onClose = useCallback(() => setMapClick(null), []);

  if (!modelsAtClick.length) return null;

  const columnDefinitions: ColumnDefinition[] = [
    {
      title: "Name",
      width: "table_cell_xs",
    },
    {
      title: "Source",
      width: "table_cell_xs",
    },
  ];

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar="Models at this location"
      maxW="800px"
      buttons={[
        <Button variant="primary" key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <RanaTable
        width={800}
        columnDefinitions={columnDefinitions}
        tableDataIds={modelsAtClick.map((row) => row.uuid)}
        isLoading={false}
        emptyElement={<RanaTable.Empty>No models to show</RanaTable.Empty>}
      >
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {modelsAtClick.map((row, index) => (
              <RanaTable.Row key={`${row.uuid}${index}`} rowId={row.uuid}>
                <RanaTable.Cell title={row.name}>
                  <ThreediModelLink threediModel={row} onClick={onClose} />
                </RanaTable.Cell>
                <RanaTable.Cell>
                  <Text>
                    {row.schematisationId !== null ? "Schematisation" : "Old model database"}
                  </Text>
                </RanaTable.Cell>
              </RanaTable.Row>
            ))}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </RanaModal>
  );
};

const MapWithModels: FC<MapWithModelsProps> = ({ threediModels, ...rest }) => {
  const extent = combineExtents(threediModels.map(threediModelExtent));

  return (
    <Box height="90%" position="relative" {...rest}>
      {threediModels.length === 0 ? (
        <Center width="100%" height="100%" position="absolute" zIndex={2000}>
          <Box padding="2rem" bg="white">
            There are no models to show.
          </Box>
        </Center>
      ) : null}
      <MapContainer bounds={extent} style={{ height: "100%" }}>
        <TileLayer
          url={`https://api.mapbox.com/styles/v1/nelenschuurmans/ck8sgpk8h25ql1io2ccnueuj6/tiles/256/{z}/{x}/{y}@2x?access_token=${mapBoxAccessToken}`}
          bounds={WORLD_EXTENT}
          noWrap
        />
        {threediModels.map((threediModel, idx) => {
          const modelExtent = threediModelExtent(threediModel);

          if (modelExtent) {
            return (
              <Polygon
                key={idx}
                positions={[
                  modelExtent.getNorthWest(),
                  modelExtent.getNorthEast(),
                  modelExtent.getSouthEast(),
                  modelExtent.getSouthWest(),
                ]}
              />
            );
          } else {
            return null;
          }
        })}
        <ClickThreediModelsMap threediModels={threediModels} />
      </MapContainer>
    </Box>
  );
};

const ModelsMap: FC = () => {
  const { organisation } = useContext(OrganisationContext);
  const navigate = useNavigate();

  const clickModelsListButton = useCallback(() => navigate(`${APP_BASE_HREF}/models`), [navigate]);

  const modelsResponse = useAllThreediModelsByOwner(organisation.uniqueId, false);

  return (
    <ExplainSideColumn
      imgUrl={ModelsMapSvg}
      imgAltDescription="Models icon"
      headerText="Models"
      explanationText={<ModelListHelpText />}
      backUrl={"/"}
    >
      <Box h="600px">
        <Button marginTop="4" onClick={clickModelsListButton} variant="primary">
          Switch to list view
        </Button>
        {modelsResponse.isSuccess ? (
          <MapWithModels
            border="0.5px solid #2C3E50"
            boxShadow="0px 3px 6px #00000029"
            marginTop="18px"
            threediModels={modelsResponse.data}
          />
        ) : (
          <Center width="100%" height="18rem">
            <Spinner size="xl" />
          </Center>
        )}
      </Box>
    </ExplainSideColumn>
  );
};

export default ModelsMap;
